/**
 * Network dataset loading with the previous app's order of preference:
 * live constellations from TLE feeds, D1 through the Worker, then the bundled
 * JSON. Failures fall through with a notice rather than an empty screen, and
 * the dataset records which source answered.
 */
import { deriveOrbitFromTLE, fetchTleCatalog, tleEpochAgeDays, type TleSatellite } from '../engine';
import type { Antenna, FeatureToggle, Mission, NetworkDataset, NetworkId } from '../types';
import { loaderApi } from './api';
import { rfimsDataset } from '@/features/rfims/loader';
import { loadSatNogsStations, type SatNogsStation } from './satnogsStations';
import {
  LIVE_ANTENNA_FILES,
  loadStaticAntennas,
  loadStaticDataset,
  loadStaticPresets,
  loadStaticTdrss,
  loadStaticTiers,
} from './staticData';

export interface LoadOptions {
  /** Skip D1 and use the bundled JSON (demo mode). */
  forceStatic?: boolean;
  /** User-facing notices (offline data, feed failures). */
  onNotice?: (notice: { level: 'info' | 'warn' | 'error'; text: string }) => void;
  signal?: AbortSignal;
}

interface LiveConstellation {
  groups: string[];
  antennasFile: string | null;
  idPrefix: string;
  agency: string;
  color: string;
  bands: string[];
  nameFilter?: RegExp;
}

/** Registry per the previous loader-data.js (appendix A, section 12). */
export const LIVE_CONSTELLATIONS: Record<string, LiveConstellation> = {
  oneweb: {
    groups: ['oneweb'],
    antennasFile: LIVE_ANTENNA_FILES.oneweb,
    idPrefix: 'oneweb_',
    agency: 'Eutelsat OneWeb',
    color: '#9c27b0',
    bands: ['Ka-band', 'Ku-band'],
  },
  stations: {
    groups: ['stations'],
    antennasFile: LIVE_ANTENNA_FILES.stations,
    idPrefix: 'station_',
    agency: 'Various',
    color: '#FFC107',
    bands: ['S-band', 'Ka-band', 'Ku-band'],
  },
  gnss: {
    groups: ['gnss'],
    antennasFile: LIVE_ANTENNA_FILES.gnss,
    idPrefix: 'gnss_',
    agency: 'Multi-national',
    color: '#4caf50',
    bands: ['L-band'],
  },
  sentinel: {
    groups: ['active'],
    antennasFile: LIVE_ANTENNA_FILES.sentinel,
    idPrefix: 'sentinel_',
    agency: 'ESA',
    color: '#2196F3',
    bands: ['X-band', 'Ka-band'],
    nameFilter: /^SENTINEL-/i,
  },
  satnogs: {
    groups: ['cubesat'],
    antennasFile: null,
    idPrefix: 'satnogs_',
    agency: 'Community / SatNOGS',
    color: '#FF9800',
    bands: ['UHF', 'VHF', 'S-band'],
  },
  aws_ground_station: {
    groups: [],
    antennasFile: LIVE_ANTENNA_FILES.aws_ground_station,
    idPrefix: 'aws_',
    agency: 'AWS',
    color: '#FF9900',
    bands: ['S-band', 'X-band'],
  },
};

const LIVE_TOGGLES: FeatureToggle[] = [
  { id: 'tdrss_relay', name: 'TDRSS Relay Modeling', enabled: false, category: 'analysis' },
  { id: 'weather_overlays', name: 'Weather Overlays', enabled: false, category: 'visualization' },
];

export async function loadNetworkDataset(
  network: NetworkId,
  opts: LoadOptions = {},
): Promise<NetworkDataset> {
  if (network === 'full_catalogue') return (await import('../ssa/data')).loadFullCatalogue(opts);
  if (network === 'rfims') return rfimsDataset();
  if (LIVE_CONSTELLATIONS[network]) {
    return loadLiveConstellation(network, opts);
  }
  if (network === 'starlink') {
    return loadStarlink(opts);
  }
  if (opts.forceStatic) return loadStaticDataset(network);
  return loadWithFallback(network, opts);
}

async function loadWithFallback(network: NetworkId, opts: LoadOptions): Promise<NetworkDataset> {
  try {
    const dataset = await loadFromApi(network);
    if (dataset.missions.length === 0 && dataset.antennas.length === 0) {
      throw new Error(`D1 has no missions or antennas for network ${network}`);
    }
    return dataset;
  } catch (err) {
    console.warn(`[loader] D1 unavailable for ${network}, using the bundled data:`, err);
    opts.onNotice?.({ level: 'info', text: 'Using offline network data.' });
    return loadStaticDataset(network);
  }
}

/** Optional D1 lookups whose documented fallback is an empty list. */
async function optional<T>(load: () => Promise<T[]>, label: string): Promise<T[]> {
  try {
    return await load();
  } catch (err) {
    console.warn(`[loader] optional ${label} unavailable:`, err);
    return [];
  }
}

export async function loadFromApi(network: NetworkId): Promise<NetworkDataset> {
  // Probe antennas first; a failure here means D1 is down and the caller falls back.
  const antennas = await loaderApi.antennas(network);
  const [missions, tiers, toggles, tdrss, scenarios, presets] = await Promise.all([
    loaderApi.missions(network),
    loaderApi.priorityTiers(network),
    loaderApi.featureToggles(),
    optional(() => loaderApi.tdrss(network), 'tdrss'),
    optional(() => loaderApi.scenarios(network), 'scenarios'),
    optional(loadStaticPresets, 'scenario presets'),
  ]);
  return { network, missions, antennas, tiers, toggles, tdrss, scenarios, presets, source: 'api' };
}

/** Mission record for one live TLE (tier 8, passive requirements). */
function missionFromTle(
  sat: TleSatellite,
  cfg: { agency: string; color: string; bands: string[] },
  nowMs: number,
): Mission {
  const derived = deriveOrbitFromTLE(sat.tle1, sat.tle2);
  const age = tleEpochAgeDays(sat.tle1, nowMs);
  return {
    id: sat.id,
    name: sat.name,
    shortName: sat.name.length > 16 ? sat.name.slice(0, 16) : sat.name,
    agency: cfg.agency,
    noradId: sat.noradId,
    priorityTier: 8,
    priorityLabel: cfg.agency,
    canBeBumped: true,
    color: cfg.color,
    preferredAntennas: [],
    acceptableAlternatives: [],
    orbit: {
      type: derived.type,
      altitudeKm: derived.altKm,
      inclinationDeg: derived.incDeg,
      periodMinutes: derived.periodMin,
      tle: { line1: sat.tle1, line2: sat.tle2 },
      tleEpochAgeDays: Number.isFinite(age) ? age : undefined,
    },
    requirements: {
      bandType: cfg.bands[0],
      bandTypes: cfg.bands,
      dataPerDayGB: 0,
      minutesPerYear: 0,
      minPassDurationMin: 1,
      slaDescription: `${cfg.agency} constellation, passive tracking`,
    },
  };
}

async function fetchGroups(
  groups: string[],
  idPrefix: string,
  signal?: AbortSignal,
): Promise<{ satellites: TleSatellite[]; sources: string[] }> {
  const sources = new Set<string>();
  const results = await Promise.all(
    groups.map(async (group) => {
      try {
        const catalog = await fetchTleCatalog(loaderApi.celestrakUrl(group), { idPrefix, signal });
        sources.add(catalog.source ?? 'CelesTrak');
        return catalog.satellites;
      } catch (err) {
        if (signal?.aborted) throw err;
        throw new Error(`Orbital feed for ${group} is unavailable. Please retry.`, { cause: err });
      }
    }),
  );
  const seen = new Set<string>();
  const merged: TleSatellite[] = [];
  for (const sat of results.flat()) {
    if (seen.has(sat.id)) continue;
    seen.add(sat.id);
    merged.push(sat);
  }
  return { satellites: merged, sources: [...sources] };
}

export async function loadLiveConstellation(network: NetworkId, opts: LoadOptions): Promise<NetworkDataset> {
  const cfg = LIVE_CONSTELLATIONS[network];
  const nowMs = Date.now();
  let stationFeedNotice: string | undefined;
  const antennasPromise: Promise<Antenna[]> = cfg.antennasFile
    ? loadStaticAntennas(cfg.antennasFile)
    : loadSatNogsStations(opts.signal).then((result) => {
        stationFeedNotice = result.notice;
        if (result.degraded && result.notice) opts.onNotice?.({ level: 'warn', text: result.notice });
        return satNogsAntennas(result.stations, cfg.bands);
      });
  const [antennas, tiers, presets, tdrss, catalog] = await Promise.all([
    antennasPromise,
    loadStaticTiers('priority-tiers.json'),
    optional(loadStaticPresets, 'scenario presets'),
    optional(loadStaticTdrss, 'tdrss-constellation.json'),
    fetchGroups(cfg.groups, cfg.idPrefix, opts.signal),
  ]);
  const filtered = cfg.nameFilter
    ? catalog.satellites.filter((s) => cfg.nameFilter!.test(s.name))
    : catalog.satellites;
  if (cfg.groups.length > 0 && filtered.length === 0) throw new Error(`No TLEs received for ${network}`);
  const missions = filtered.map((s) => missionFromTle(s, cfg, nowMs));
  reportFeedSources(catalog.sources, opts);
  return {
    network,
    missions,
    antennas,
    tiers,
    toggles: LIVE_TOGGLES,
    tdrss,
    scenarios: [],
    presets,
    source: 'live',
    catalogSources: catalog.sources,
    stationFeedNotice,
  };
}

/** SatNOGS ground stations as 1 m amateur antennas (online or testing, newest first). */
function satNogsAntennas(stations: SatNogsStation[], bands: string[]): Antenna[] {
  return stations
    .filter(
      (s) =>
        s.lat != null &&
        s.lng != null &&
        (!s.status ||
          s.status === 'Online' ||
          s.status === 'Testing' ||
          s.status === 'online' ||
          s.status === 'testing'),
    )
    .sort((a, b) => (b.last_seen ?? '').localeCompare(a.last_seen ?? ''))
    .map((s) => ({
      id: `satnogs_${s.id}`,
      name: s.name,
      shortName: s.name.length > 16 ? s.name.slice(0, 16) : s.name,
      network: 'SatNOGS',
      location: { lat: s.lat as number, lon: s.lng as number, name: s.name, country: '' },
      capabilities: {
        diameterM: 1,
        bands,
        maxHoursPerDay: 24,
        maintenanceWindowHrs: 0,
        uplinkCapable: false,
        type: 'Amateur',
      },
      coverage: {
        elevationMinDeg: 5,
        elevationMaxDeg: 90,
        azimuthMinDeg: 0,
        azimuthMaxDeg: 360,
        effectiveRadiusKm: 1500,
      },
      performance: { averageSuccessRate: 0.95, weatherImpactFactor: 0.05, currentUtilization: 0 },
      enabled: true,
    }));
}

const STARLINK = { agency: 'SpaceX', color: '#00bcd4', bands: ['Ka-band', 'Ku-band'] };

export async function loadStarlink(opts: LoadOptions): Promise<NetworkDataset> {
  const nowMs = Date.now();
  const [antennas, tiers, presets, tdrss] = await Promise.all([
    loadStaticAntennas('antennas-starlink.json'),
    loadStaticTiers('priority-tiers.json'),
    optional(loadStaticPresets, 'scenario presets'),
    optional(loadStaticTdrss, 'tdrss-constellation.json'),
  ]);
  let sats: TleSatellite[];
  let catalogSource: string;
  try {
    const catalog = await fetchTleCatalog(loaderApi.celestrakUrl('starlink-sup', { supplemental: true }), {
      idPrefix: 'starlink_',
      signal: opts.signal,
    });
    sats = catalog.satellites;
    catalogSource = catalog.source ?? 'CelesTrak';
    if (!sats.length) throw new Error('Empty supplemental catalogue');
  } catch (err) {
    if (opts.signal?.aborted) throw err;
    console.warn('[loader] Starlink supplemental TLEs failed, trying the standard group:', err);
    const catalog = await fetchTleCatalog(loaderApi.celestrakUrl('starlink'), {
      idPrefix: 'starlink_',
      signal: opts.signal,
    });
    sats = catalog.satellites;
    catalogSource = catalog.source ?? 'CelesTrak';
  }
  if (sats.length === 0) throw new Error('No Starlink TLEs received');
  reportFeedSources([catalogSource], opts);
  const missions = sats.map((s) => {
    const m = missionFromTle(s, STARLINK, nowMs);
    const short = `SL-${s.name.replace(/^STARLINK-/i, '')}`;
    return {
      ...m,
      shortName: short.length > 20 ? short.slice(0, 20) : short,
      priorityTier: 10,
      priorityLabel: 'Passive (commercial)',
    };
  });
  return {
    network: 'starlink',
    catalogSources: [catalogSource],
    missions,
    antennas,
    tiers,
    toggles: LIVE_TOGGLES,
    tdrss,
    scenarios: [],
    presets,
    source: 'live',
  };
}

function reportFeedSources(sources: string[], opts: LoadOptions): void {
  if (sources.some((s) => s.includes('mirror')))
    opts.onNotice?.({
      level: 'warn',
      text: `Direct orbital feed unavailable. Using ${sources.join(', ')}; orbital predictions depend on TLE age.`,
    });
}

/** Standard Starlink group URL for the periodic refresh (2.5 h in the previous app). */
export const STARLINK_REFRESH_URL = loaderApi.celestrakUrl('starlink');
export const STARLINK_REFRESH_MS = 9_000_000;
